import { useRef, useState } from 'react'
import { sectionAnchors } from '../../../config/routes'

type Faq = {
  question: string
  answer: string
}

// Seeded from common teacher questions + the scanning tips.
const FAQS: Faq[] = [
  {
    question: 'Do I need special answer sheets or a scantron machine?',
    answer:
      'No. QuickScore reads plain bubble (OMR) forms and handwritten answers in any layout — no rigid template, no special paper, and no scantron hardware. Your phone is the scanner.',
  },
  {
    question: 'Can it really read handwriting?',
    answer:
      "Yes. AI image recognition reads handwritten multiple choice, true/false, identification, enumeration, and fill-in-the-blank answers, and it transcribes open responses so you can score them. Anything it isn't confident about is flagged so you can check it in seconds.",
  },
  {
    question: 'Can it handle essay or open-ended answers?',
    answer:
      "Yes. Mark those items as an open response in your answer key — there's no correct answer to match, so QuickScore transcribes what the student wrote and hands it to you to score during review. Every other question type is still graded for you.",
  },
  {
    question: "How does grading get the student's name?",
    answer:
      "The name is extracted straight from the answer sheet — there's no roster to build. Just make sure the name area is included when you scan.",
  },
  {
    question: 'What do I get at the end?',
    answer:
      'Per-student scores you can review and adjust, plus class results and a per-question item analysis you can export to CSV and email to yourself.',
  },
  {
    question: 'How does pricing work?',
    answer:
      'You buy credits and each scanned sheet uses one. Start with free credits to grade your first class, then top up whenever you need more.',
  },
]

// Accessible accordion — ported from the prototype's inline JS
// (measures scrollHeight to animate max-height, docs/quickscore/spec/…prototype.html:632-639)
// into React refs/state. Multiple items can be open at once, matching the prototype.
export const FaqSection = () => {
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set())
  const answerRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const toggle = (question: string) => {
    setOpenQuestions((prev) => {
      const next = new Set(prev)
      if (next.has(question)) {
        next.delete(question)
      } else {
        next.add(question)
      }
      return next
    })
  }

  return (
    <section id={sectionAnchors.faq.slice(1)}>
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">FAQ</span>
          <h2>Questions teachers ask</h2>
        </div>
        <div className="faq">
          {FAQS.map((faq) => {
            const isOpen = openQuestions.has(faq.question)
            return (
              <div className={`qa${isOpen ? ' open' : ''}`} key={faq.question}>
                <button type="button" aria-expanded={isOpen} onClick={() => toggle(faq.question)}>
                  {faq.question}
                  <span className="plus"></span>
                </button>
                <div
                  className="a"
                  ref={(el) => {
                    answerRefs.current[faq.question] = el
                  }}
                  style={{
                    maxHeight: isOpen ? `${answerRefs.current[faq.question]?.scrollHeight ?? 500}px` : undefined,
                  }}
                >
                  <p>{faq.answer}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
